import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "./shared";
import { longDate } from "@/lib/training";

const OPTS = [["present", "Present", "bg-emerald-500 text-[#0B0F19]"], ["absent", "Absent", "bg-red-500 text-white"], ["excused", "Excused", "bg-amber-400 text-[#0B0F19]"]];

export default function AttendanceTab({ ctx }) {
  const { programs, token } = ctx;
  const live = programs.filter((p) => !["draft", "canceled", "archived"].includes(p.status) && (p.registered > 0 || p.dropin_enabled));
  const [pid, setPid] = useState(live[0]?.id || "");
  const [data, setData] = useState(null);
  const [sid, setSid] = useState("");
  const [saving, setSaving] = useState("");
  const h = { headers: { "X-Admin-Token": token } };

  useEffect(() => {
    if (!pid) return;
    let live = true;
    setData(null);
    axios.get(`${API}/admin/attendance/${pid}`, h).then((r) => {
      if (!live) return;
      setData(r.data);
      const today = new Date().toISOString().slice(0, 10);
      setSid((r.data.sessions.find((s) => s.date >= today) || r.data.sessions[r.data.sessions.length - 1] || {}).id || "");
    });
    return () => { live = false; };
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = async (rid, status) => {
    setSaving(rid);
    const cur = data.roster.find((r) => r.registration_id === rid)?.attendance[sid];
    const next = cur === status ? "" : status;
    try {
      await axios.post(`${API}/admin/attendance`, { program_id: pid, session_id: sid, registration_id: rid, status: next }, h);
      setData((d) => ({ ...d, roster: d.roster.map((r) => (r.registration_id === rid ? { ...r, attendance: { ...r.attendance, [sid]: next } } : r)) }));
    } finally {
      setSaving("");
    }
  };

  return (
    <>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Attendance</h1>
      <select data-testid="attendance-offering" value={pid} onChange={(e) => setPid(e.target.value)} className="mt-6 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-base">
        {live.length === 0 && <option className="bg-[#0B0F19]">No offerings with registered athletes</option>}
        {live.map((p) => <option key={p.id} value={p.id} className="bg-[#0B0F19]">{p.title} · {p.age_group} · {p.start_date}</option>)}
      </select>
      {pid && !data && <Loader2 className="mt-8 animate-spin text-white/40" />}
      {data && (
        <>
          <div data-testid="attendance-sessions" className="mt-5 flex gap-2 overflow-x-auto pb-2">
            {data.sessions.map((s, i) => (
              <button key={s.id} data-testid={`attendance-session-${i}`} onClick={() => setSid(s.id)} className={`shrink-0 rounded-full px-4 py-2.5 text-sm font-bold ${sid === s.id ? "bg-white text-[#0B0F19]" : "border border-white/15 text-white/70"}`}>
                #{i + 1} · {longDate(s.date).replace(/, \d{4}$/, "")}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {data.roster.length === 0 && <p className="text-white/50">No registered athletes.</p>}
            {data.roster.filter((r) => !r.dropin_session_id || r.dropin_session_id === sid).map((r) => (
              <div key={r.registration_id} data-testid={`attendance-row-${r.registration_id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-base font-bold text-white">{r.athlete_name}{r.dropin_session_id && <span data-testid={`dropin-badge-${r.registration_id}`} className="ml-2 rounded-full bg-[#2563EB]/20 px-2 py-0.5 text-[10px] font-bold uppercase text-[#93c5fd]">Drop-in</span>}</p>
                  <p className="text-xs font-medium text-white/45">{r.parent_name}{r.phone ? ` · ${r.phone}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  {OPTS.map(([v, l, on]) => (
                    <button key={v} data-testid={`attendance-${v}-${r.registration_id}`} disabled={saving === r.registration_id} onClick={() => mark(r.registration_id, v)}
                      className={`min-w-[84px] rounded-xl px-3 py-3 text-sm font-extrabold ${r.attendance[sid] === v ? on : "border border-white/15 text-white/60"}`}>{l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <AddDropIn pid={pid} sid={sid} h={h} onAdded={() => axios.get(`${API}/admin/attendance/${pid}`, h).then((r) => setData(r.data))} />
        </>
      )}
    </>
  );
}

function AddDropIn({ pid, sid, h, onAdded }) {
  const [f, setF] = useState({ athlete_name: "", parent_name: "", parent_email: "", payment_status: "paid_offline" });
  const [msg, setMsg] = useState("");
  const add = async () => {
    setMsg("");
    try { await axios.post(`${API}/admin/dropins`, { ...f, program_id: pid, session_id: sid }, h); setF({ ...f, athlete_name: "" }); setMsg("Added to this session."); onAdded(); } catch (e) { setMsg(e?.response?.data?.detail || "Could not add."); }
  };
  const c = "bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm";
  return (
    <div data-testid="add-dropin" className="mt-6 rounded-2xl border border-dashed border-white/15 p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-white/40">Add an approved drop-in to this session</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input data-testid="dropin-athlete" placeholder="Athlete name" value={f.athlete_name} onChange={(e) => setF({ ...f, athlete_name: e.target.value })} className={c} />
        <input data-testid="dropin-parent" placeholder="Parent name" value={f.parent_name} onChange={(e) => setF({ ...f, parent_name: e.target.value })} className={c} />
        <input data-testid="dropin-email" placeholder="Parent email" value={f.parent_email} onChange={(e) => setF({ ...f, parent_email: e.target.value })} className={c} />
        <select data-testid="dropin-payment" value={f.payment_status} onChange={(e) => setF({ ...f, payment_status: e.target.value })} className={c}><option value="paid_offline" className="bg-[#0B0F19]">Paid offline</option><option value="complimentary" className="bg-[#0B0F19]">Complimentary</option><option value="unpaid" className="bg-[#0B0F19]">Unpaid</option></select>
        <button data-testid="dropin-add-btn" onClick={add} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-bold text-white">Add drop-in</button>
      </div>
      {msg && <p className="mt-2 text-xs font-bold text-white/70">{msg}</p>}
    </div>
  );
}
